import { getImageDimensions } from "@sanity/asset-utils";
import type { ImageUrlBuilder } from "@sanity/image-url";
import type { SanityImageAsset, SanityImageCrop } from "@/types/sanity.types";
import type { ImageUrlOptions } from "./types";

const DEFAULT_IMAGE_CONFIG = {
  auto: "format",
  fit: "max",
  quality: 75
};

/**
 * Build a Sanity image URL with the given dimensions and options
 */
export const buildSrc = (
  builder: ImageUrlBuilder,
  asset: SanityImageAsset,
  options: ImageUrlOptions
): string => {
  const dimensions = getImageDimensions(asset);
  const { width, height, fit = "max", quality = 75 } = options;

  // Use provided dimensions, falling back to original (capped at original size)
  const finalWidth = Math.min(
    width || dimensions.width,
    dimensions.width
  );
  const origRatio = dimensions.width / dimensions.height;
  const finalHeight = height
    ? Math.min(height, dimensions.height)
    : Math.round(finalWidth / origRatio);

  return buildImageUrl(builder, asset, {
    width: finalWidth,
    height: finalHeight,
    fit,
    quality
  });
};

/**
 * Build the complete Sanity CDN URL with all parameters
 */
const buildImageUrl = (
  builder: ImageUrlBuilder,
  asset: SanityImageAsset,
  params: ImageUrlOptions
): string => {
  const { fit, width, height, quality } = params;

  // Sanity CDN workarounds
  let sanityParams: Record<string, any> = { fit, width, quality };

  // Only include height for non-clip fit modes
  if (fit !== "clip") {
    sanityParams = { ...sanityParams, height };
  }

  // Hack: use 'max' instead of 'clip' due to Sanity CDN issue
  if (fit === "clip") {
    sanityParams = { ...sanityParams, fit: "max" };
  }

  return Object.entries({
    ...DEFAULT_IMAGE_CONFIG,
    ...sanityParams
  })
    .reduce<ImageUrlBuilder>((acc, [key, value]) => {
      if (!value) return acc;

      if (Array.isArray(value)) {
        return (acc as any)[key](...value);
      }

      return (acc as any)[key](value);
    }, builder.image(asset))
    .url()!
    .replace(/, /g, ",");
};

/**
 * Calculate dimensions after applying crop
 * Accepts both SanityImageCrop and flexible crop objects
 */
export function calculateCroppedDimensions(
  originalWidth: number,
  originalHeight: number,
  crop?: Partial<SanityImageCrop>
): { width: number; height: number } {
  if (!crop) {
    return { width: originalWidth, height: originalHeight };
  }

  const { top = 0, bottom = 0, left = 0, right = 0 } = crop;

  return {
    width: Math.round(originalWidth * (1 - left - right)),
    height: Math.round(originalHeight * (1 - top - bottom))
  };
}

/**
 * Generate responsive sizes attribute based on breakpoints.
 * The last entry's imageWidth is used as the default (no media condition)
 * so the browser doesn't fall back to 100vw on larger viewports.
 */
export function generateSizes(
  breakpoints: { maxWidth: string; imageWidth: string }[],
  defaultSize?: string
): string {
  const conditions = breakpoints
    .map(({ maxWidth, imageWidth }) => `(max-width: ${maxWidth}) ${imageWidth}`)
    .join(", ");

  // Append default size (without media condition) so the browser
  // doesn't assume 100vw for viewports beyond the last breakpoint
  const fallback = defaultSize ?? breakpoints[breakpoints.length - 1]?.imageWidth;
  return fallback ? `${conditions}, ${fallback}` : conditions;
}

/**
 * Common responsive sizes for blog images
 */
export const RESPONSIVE_SIZES = {
  // Full width hero images
  hero: generateSizes([
    { maxWidth: "640px", imageWidth: "100vw" },
    { maxWidth: "768px", imageWidth: "100vw" },
    { maxWidth: "1024px", imageWidth: "100vw" },
    { maxWidth: "1280px", imageWidth: "100vw" }
  ], "100vw"),

  // Featured images (2/3 width on desktop)
  featured: generateSizes([
    { maxWidth: "640px", imageWidth: "100vw" },
    { maxWidth: "768px", imageWidth: "100vw" },
    { maxWidth: "1024px", imageWidth: "66vw" }
  ], "66vw"),

  // Content images (within PostPage article column)
  // Article content max width: (1440 - 144) × 0.7 - 24 ≈ 880px
  content: generateSizes([
    { maxWidth: "768px", imageWidth: "100vw" },
    { maxWidth: "1024px", imageWidth: "70vw" }
  ], "880px"),

  contentSmall: generateSizes([
    { maxWidth: "768px", imageWidth: "100vw" },
    { maxWidth: "1024px", imageWidth: "50vw" }
  ], "600px"),

  // Card images (fixed layout, not viewport-relative)
  card: generateSizes([
    { maxWidth: "640px", imageWidth: "100vw" },
    { maxWidth: "1024px", imageWidth: "50vw" }
  ], "400px"),

  // Thumbnail images
  thumbnail: generateSizes([
    { maxWidth: "640px", imageWidth: "50vw" },
    { maxWidth: "768px", imageWidth: "33vw" },
    { maxWidth: "1024px", imageWidth: "25vw" }
  ], "25vw")
};
